import {
  multiplyMoney,
  roundMoney,
  subtractMoney,
  sumMoney,
  toMoneyNumber,
} from '@erp/shared-utils';
import {
  clausesForContract,
  type Clause,
  type ClauseRule,
} from '../../data/clauses';
import { getSupplier } from '../../data/suppliers';
import { addDays, fmtDate } from '../../data/dates';
import { fmtMoney, fmtMoneyCompact, fmtNumber } from '../../data/format';
import {
  perUnitText,
  pricePerText,
  priceSlashText,
  qtyText,
} from './unit-text';
import {
  INVOICES,
  invoiceTotal,
  type Invoice,
  type InvoiceId,
  type InvoiceLine,
  type LineKind,
} from './invoices-data';

/**
 * Invoice compliance engine. Pure functions: given an invoice, evaluate it
 * against the enforced clauses of its agreements (`Clause.rule` from
 * data/clauses.ts) and against the PO and receipt (3-way match).
 *
 * Severity policy: charging more than a hard price, cap or term in the
 * agreement is a violation. Credits owed to Pillar (uptime credits, rebates) and
 * checks that rest on third-party data (labor markup against pay rates,
 * overtime approvals) are warnings: Pillar should act on them, but they do not
 * block payment of the undisputed amount.
 */

export type CheckStatus = 'pass' | 'violation' | 'warning';

export type CheckCategory =
  | 'standard'
  | 'terms'
  | 'price'
  | 'freight'
  | 'duties'
  | 'rate_card'
  | 'not_to_exceed'
  | 'expense_cap'
  | 'surcharge'
  | 'proof'
  | 'markup'
  | 'pass_through'
  | 'sla_credit'
  | 'rebate'
  | 'early_pay';

export interface CheckResult {
  id: string;
  label: string;
  category: CheckCategory;
  status: CheckStatus;
  /** What the invoice says. */
  invoiceValue: string;
  /** What the agreement requires. */
  contractValue: string;
  /** The clause enforced. Null for the standard PO, receipt and vendor checks. */
  clauseId: string | null;
  /** Other clauses that bear on the same finding. */
  relatedClauseIds: string[];
  /** Recoverable or owed dollars. Zero for timing findings and passes. */
  impactAmount: number;
  impactNote: string | null;
  /** Analyst-facing explanation. */
  explanation: string;
  /** Supplier-facing sentence used in dispute emails. */
  supplierStatement: string;
  /** Invoice lines to highlight on the document. */
  lineIds: string[];
  lineCell: 'row' | 'price';
  /** Header field to highlight on the document. */
  headerField: 'terms' | 'total' | null;
  /** Discount available when paid early (not a recoverable amount). */
  opportunity?: number;
}

export interface CheckSummary {
  total: number;
  passed: number;
  violations: number;
  warnings: number;
  /** Sum of the impact of every flagged check. */
  recoverable: number;
  status: 'violation' | 'warning' | 'compliant';
}

export interface MatchRow {
  lineId: string;
  description: string;
  unit: string;
  poLineId: string | null;
  poQuantity: number | null;
  poPrice: number | null;
  receivedQuantity: number | null;
  invoiceQuantity: number;
  invoicePrice: number;
  invoiceAmount: number;
  quantityStatus: 'match' | 'over' | 'no_po_line';
  priceStatus: 'match' | 'variance' | 'not_on_po';
  /** Dollars above the PO: price variance x quantity, or the whole line when it is not on the PO. */
  variance: number;
}

export interface ThreeWayMatch {
  poId: string;
  receiptId: string;
  receiptKind: string;
  rows: MatchRow[];
  quantitiesMatch: boolean;
  /** Rows with a price variance or no PO line. */
  varianceCount: number;
}

export interface InvoiceAnalysis {
  invoice: Invoice;
  total: number;
  results: CheckResult[];
  flagged: CheckResult[];
  passed: CheckResult[];
  summary: CheckSummary;
  match: ThreeWayMatch;
  invoiceDue: Date;
  contractDue: Date;
  /** Days the contract due date is later than the due date on the invoice. */
  termsGapDays: number;
}

const CENT = 0.005;

function money(value: number): number {
  return toMoneyNumber(roundMoney(value, 2, 'ROUND_HALF_UP'));
}

function times(a: number, b: number): number {
  return money(toMoneyNumber(multiplyMoney(a, b)));
}

function plus(values: number[]): number {
  return toMoneyNumber(sumMoney(values));
}

function minus(a: number, b: number): number {
  return toMoneyNumber(subtractMoney(a, b));
}

function usd(value: number): string {
  return fmtMoney(value, { cents: true });
}

function pctText(value: number): string {
  return `${value.toFixed(1)}%`;
}

function section(clause: Clause): string {
  return `§${clause.section}`;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled clause rule: ${JSON.stringify(value)}`);
}

function linesOfKind(invoice: Invoice, kind: LineKind): InvoiceLine[] {
  return invoice.lines.filter((line) => line.kind === kind);
}

function amountOf(lines: InvoiceLine[]): number {
  return plus(lines.map((line) => line.amount));
}

interface RuleClause {
  clause: Clause;
  rule: ClauseRule;
}

/** Enforced clauses with a machine-checkable rule, governing agreement first. */
export function enforcedRuleClauses(invoice: Invoice): RuleClause[] {
  const found: RuleClause[] = [];
  for (const contractId of invoice.contractIds) {
    for (const clause of clausesForContract(contractId)) {
      if (clause.enforced && clause.rule) {
        found.push({ clause, rule: clause.rule });
      }
    }
  }
  return found;
}

function relatedClauseIds(
  invoice: Invoice,
  clause: Clause,
  kinds: Clause['kind'][],
): string[] {
  const ids: string[] = [];
  for (const contractId of invoice.contractIds) {
    for (const other of clausesForContract(contractId)) {
      if (other.id !== clause.id && kinds.includes(other.kind)) {
        ids.push(other.id);
      }
    }
  }
  return ids;
}

interface ResultInput {
  clause: Clause;
  category: CheckCategory;
  label: string;
  status: CheckStatus;
  invoiceValue: string;
  contractValue: string;
  explanation: string;
  supplierStatement?: string;
  impactAmount?: number;
  impactNote?: string | null;
  related?: string[];
  lineIds?: string[];
  lineCell?: 'row' | 'price';
  headerField?: 'terms' | 'total' | null;
  opportunity?: number;
}

function result(input: ResultInput): CheckResult {
  return {
    id: `${input.category}:${input.clause.id}`,
    label: input.label,
    category: input.category,
    status: input.status,
    invoiceValue: input.invoiceValue,
    contractValue: input.contractValue,
    clauseId: input.clause.id,
    relatedClauseIds: input.related ?? [],
    impactAmount: input.impactAmount ?? 0,
    impactNote: input.impactNote ?? null,
    explanation: input.explanation,
    supplierStatement: input.supplierStatement ?? '',
    lineIds: input.lineIds ?? [],
    lineCell: input.lineCell ?? 'row',
    headerField: input.headerField ?? null,
    opportunity: input.opportunity,
  };
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** Due date printed on the invoice: terms counted from the invoice date. */
export function invoiceDueDate(invoice: Invoice): Date {
  return addDays(invoice.invoiceDate, invoice.statedTerms.netDays);
}

/** Last day to take the early-payment discount, counted from receipt. */
export function earlyPayDeadline(invoice: Invoice): Date | null {
  const discount = invoice.statedTerms.discount;
  return discount ? addDays(invoice.invoiceDate, discount.days) : null;
}

/** Net days from the governing payment-terms clause, if one is enforced. */
function contractNetDays(invoice: Invoice): number | null {
  for (const { rule } of enforcedRuleClauses(invoice)) {
    if (rule.kind === 'payment_terms' && !rule.discount) return rule.netDays;
  }
  return null;
}

/** Due date under the agreement: net days from receipt of a valid invoice. */
export function contractDueDate(invoice: Invoice): Date {
  const netDays = contractNetDays(invoice) ?? invoice.statedTerms.netDays;
  return addDays(invoice.invoiceDate, netDays);
}

// ---------------------------------------------------------------------------
// Rule evaluators
// ---------------------------------------------------------------------------

function termsCheck(
  invoice: Invoice,
  clause: Clause,
  netDays: number,
): CheckResult[] {
  const statedDays = invoice.statedTerms.netDays;
  const invoiceDue = invoiceDueDate(invoice);
  const contractDue = addDays(invoice.invoiceDate, netDays);
  const total = invoiceTotal(invoice);
  const tooShort = statedDays < netDays;
  const gap = netDays - statedDays;
  return [
    result({
      clause,
      category: 'terms',
      label: 'Payment terms',
      status: tooShort ? 'violation' : 'pass',
      invoiceValue: `${invoice.statedTerms.label}, due ${fmtDate(invoiceDue)}`,
      contractValue: `Net ${netDays} from receipt, due ${fmtDate(contractDue)}`,
      impactNote: tooShort
        ? `Pay ${gap} days later: keeps ${usd(total)} in cash ${gap} more days`
        : null,
      explanation: tooShort
        ? `The invoice asks for payment ${gap} days earlier than the agreement allows. ${section(clause)} sets Net ${netDays} from receipt of a valid invoice, and a shorter term printed on an invoice has no effect.`
        : `Invoice terms (${invoice.statedTerms.label}) are no shorter than ${section(clause)} (Net ${netDays}).`,
      supplierStatement: `The invoice states ${invoice.statedTerms.label}. Under ${section(clause)} payment is due within ${netDays} days after receipt of a valid invoice, so this invoice is scheduled for payment on ${fmtDate(contractDue)}.`,
      headerField: tooShort ? 'terms' : null,
    }),
  ];
}

function earlyPayCheck(
  invoice: Invoice,
  clause: Clause,
  discount: { pct: number; days: number },
  netDays: number,
): CheckResult[] {
  const total = invoiceTotal(invoice);
  const saving = times(total, discount.pct / 100);
  const deadline = addDays(invoice.invoiceDate, discount.days);
  const annualised =
    (discount.pct / (100 - discount.pct)) *
    (365 / (netDays - discount.days)) *
    100;
  return [
    result({
      clause,
      category: 'early_pay',
      label: 'Early-payment discount',
      status: 'pass',
      invoiceValue: invoice.statedTerms.discount
        ? `${invoice.statedTerms.label} stated`
        : 'No discount stated',
      contractValue: `${discount.pct}% if paid within ${discount.days} days (by ${fmtDate(deadline)})`,
      impactNote: `Pay on ${fmtDate(deadline)} to capture ${usd(saving)}`,
      explanation: `A ${discount.pct}% discount is available for paying within ${discount.days} days (otherwise due in ${netDays}). Paying on day ${discount.days} instead of day ${netDays} saves ${usd(saving)}, equivalent to earning ${pctText(annualised)} a year.`,
      opportunity: saving,
    }),
  ];
}

function unitPriceCheck(
  invoice: Invoice,
  clause: Clause,
  rule: Extract<ClauseRule, { kind: 'unit_price' }>,
): CheckResult[] {
  const priced = invoice.lines.filter((line) => line.unit === rule.unit);
  if (priced.length === 0) return [];
  const over = priced.filter(
    (line) => line.unitPrice - rule.price > CENT && line.kind !== 'fee',
  );
  const variance = plus(
    over.map((line) => times(line.quantity, minus(line.unitPrice, rule.price))),
  );
  const isCap = clause.kind === 'price_adjustment';
  const isSubscription = rule.unit === 'month';
  const label = isCap
    ? 'Unit price vs price increase cap'
    : isSubscription
      ? 'Subscription fee vs agreement'
      : 'Unit price vs agreement';
  const [first] = over;
  const shownLine = first ?? priced[0];
  const flagged = over.length > 0;
  return [
    result({
      clause,
      category: 'price',
      label,
      status: flagged ? 'violation' : 'pass',
      invoiceValue: priceSlashText(shownLine.unitPrice, rule.unit),
      contractValue: `${isCap ? 'Max ' : ''}${priceSlashText(rule.price, rule.unit)}`,
      impactAmount: flagged ? variance : 0,
      impactNote: flagged
        ? `${qtyText(first.quantity, rule.unit)} at ${pricePerText(minus(first.unitPrice, rule.price), rule.unit)} over the ${isCap ? 'cap' : 'agreed price'}`
        : null,
      explanation: flagged
        ? `${shownLine.description} is billed at ${pricePerText(shownLine.unitPrice, rule.unit)}. ${section(clause)} ${isCap ? 'caps the 2026 price' : 'fixes the price'} at ${pricePerText(rule.price, rule.unit)}, so ${usd(variance)} is overbilled.`
        : `Billed price matches ${section(clause)} (${pricePerText(rule.price, rule.unit)}).`,
      supplierStatement: flagged
        ? `${shownLine.description} is billed at ${pricePerText(shownLine.unitPrice, rule.unit)}. The ${isCap ? 'maximum price' : 'price'} under ${section(clause)} is ${pricePerText(rule.price, rule.unit)}. Variance: ${qtyText(first.quantity, rule.unit)} at ${pricePerText(minus(first.unitPrice, rule.price), rule.unit)} = ${usd(variance)}.`
        : '',
      lineIds: over.map((line) => line.id),
      lineCell: 'price',
    }),
  ];
}

function freightCheck(
  invoice: Invoice,
  clause: Clause,
  incoterm: 'DAP' | 'DDP' | 'FCA',
): CheckResult[] {
  if (incoterm === 'FCA') return [];
  const freight = linesOfKind(invoice, 'freight');
  const amount = amountOf(freight);
  const flagged = freight.length > 0;
  return [
    result({
      clause,
      category: 'freight',
      label: 'Freight and delivery terms',
      status: flagged ? 'violation' : 'pass',
      invoiceValue: flagged
        ? `Freight billed: ${usd(amount)}`
        : 'No freight charge',
      contractValue:
        incoterm === 'DDP'
          ? 'Delivered, freight and duties included in the price'
          : 'Delivered, freight included in the price',
      impactAmount: amount,
      impactNote: flagged
        ? `${usd(amount)} is not payable: freight is included in the price`
        : null,
      explanation: flagged
        ? `The supplier delivers with freight included, so it bears freight and insurance to the Pillar dock. ${section(clause)} leaves no room for a separate freight and handling charge.`
        : `No separate freight billed, consistent with delivery with freight included (${section(clause)}).`,
      supplierStatement: flagged
        ? `A freight and handling charge of ${usd(amount)} is billed. Under ${section(clause)} the supplier delivers with freight included in the price.`
        : '',
      lineIds: freight.map((line) => line.id),
    }),
  ];
}

function dutiesCheck(invoice: Invoice, clause: Clause): CheckResult[] {
  const duties = linesOfKind(invoice, 'duty');
  const amount = amountOf(duties);
  const goods = amountOf(linesOfKind(invoice, 'goods'));
  const flagged = duties.length > 0;
  const share = goods > 0 ? (amount / goods) * 100 : 0;
  return [
    result({
      clause,
      category: 'duties',
      label: 'Duties and tariffs',
      status: flagged ? 'violation' : 'pass',
      invoiceValue: flagged
        ? `Tariff surcharge billed: ${usd(amount)} (${pctText(share)} of goods)`
        : 'No duty or tariff charge',
      contractValue: 'Borne by the supplier, included in the price',
      impactAmount: amount,
      impactNote: flagged
        ? `Reject the ${usd(amount)} line and pay the balance`
        : null,
      explanation: flagged
        ? `The supplier delivers with duties and tariffs paid, and ${section(clause)} makes all duties and tariffs, including new or increased ones, the supplier's cost. A tariff surcharge cannot be added to an invoice.`
        : `No duties or tariffs billed (${section(clause)}).`,
      supplierStatement: flagged
        ? `The invoice adds a tariff surcharge of ${usd(amount)} (${pctText(share)} of the goods value). Under ${section(clause)} all duties and tariffs, including any introduced after the effective date, are borne by the supplier and included in the price, and the supplier delivers with duties and tariffs paid. We are rejecting this line.`
        : '',
      related: relatedClauseIds(invoice, clause, ['incoterms']),
      lineIds: duties.map((line) => line.id),
    }),
  ];
}

function fuelCheck(
  invoice: Invoice,
  clause: Clause,
  maxPct: number,
): CheckResult[] {
  const fuel = linesOfKind(invoice, 'fuel_surcharge');
  if (fuel.length === 0) return [];
  const base = amountOf(linesOfKind(invoice, 'line_haul'));
  const billed = amountOf(fuel);
  const allowed = times(base, maxPct / 100);
  const over = minus(billed, allowed);
  const flagged = over > CENT;
  const billedPct = base > 0 ? (billed / base) * 100 : 0;
  return [
    result({
      clause,
      category: 'surcharge',
      label: 'Fuel surcharge cap',
      status: flagged ? 'violation' : 'pass',
      invoiceValue: `${pctText(billedPct)} of base freight (${usd(billed)})`,
      contractValue: `Max ${maxPct}% of base freight (${usd(allowed)})`,
      impactAmount: flagged ? over : 0,
      impactNote: flagged ? `${usd(over)} over the cap` : null,
      explanation: flagged
        ? `The fuel surcharge is ${pctText(billedPct)} of the ${usd(base)} base freight charge. ${section(clause)} follows the U.S. Department of Energy diesel price table but never exceeds ${maxPct}%, so ${usd(over)} is not payable.`
        : `Fuel surcharge is within the ${maxPct}% cap (${section(clause)}).`,
      supplierStatement: flagged
        ? `The fuel surcharge of ${usd(billed)} is ${pctText(billedPct)} of base freight (${usd(base)}). ${section(clause)} caps the surcharge at ${maxPct}% (${usd(allowed)}), so ${usd(over)} is not payable.`
        : '',
      lineIds: flagged ? fuel.map((line) => line.id) : [],
    }),
  ];
}

function expenseCapCheck(
  invoice: Invoice,
  clause: Clause,
  maxPct: number,
): CheckResult[] {
  const expenses = linesOfKind(invoice, 'expense');
  if (expenses.length === 0) return [];
  const fees = amountOf(linesOfKind(invoice, 'labor'));
  const billed = amountOf(expenses);
  const allowed = times(fees, maxPct / 100);
  const over = minus(billed, allowed);
  const flagged = over > CENT;
  const billedPct = fees > 0 ? (billed / fees) * 100 : 0;
  return [
    result({
      clause,
      category: 'expense_cap',
      label: 'Expense cap',
      status: flagged ? 'violation' : 'pass',
      invoiceValue: `${usd(billed)} (${pctText(billedPct)} of fees)`,
      contractValue: `Max ${maxPct}% of fees (${usd(allowed)})`,
      impactAmount: flagged ? over : 0,
      impactNote: flagged ? `${usd(over)} over the cap` : null,
      explanation: flagged
        ? `Expenses are ${pctText(billedPct)} of the ${usd(fees)} in professional fees. ${section(clause)} limits reimbursable expenses to ${maxPct}% of fees, so ${usd(over)} is not payable.`
        : `Expenses are within ${maxPct}% of fees (${section(clause)}).`,
      supplierStatement: flagged
        ? `Expenses of ${usd(billed)} are ${pctText(billedPct)} of professional fees (${usd(fees)}). ${section(clause)} limits expenses to ${maxPct}% of fees (${usd(allowed)}). The ${usd(over)} above the cap is not payable.`
        : '',
      lineIds: flagged ? expenses.map((line) => line.id) : [],
    }),
  ];
}

function markupCheck(
  invoice: Invoice,
  clause: Clause,
  maxPct: number,
): CheckResult[] {
  const labor = invoice.lines.filter(
    (line) => line.kind === 'labor' && line.payRate !== undefined,
  );
  if (labor.length === 0) return [];
  let excess = 0;
  let billed = 0;
  let payCost = 0;
  const flaggedIds: string[] = [];
  for (const line of labor) {
    const pay = line.payRate ?? 0;
    const capRate = money(pay * (1 + maxPct / 100));
    billed = plus([billed, line.amount]);
    payCost = plus([payCost, times(line.quantity, pay)]);
    if (line.unitPrice - capRate > CENT) {
      excess = plus([
        excess,
        times(line.quantity, minus(line.unitPrice, capRate)),
      ]);
      flaggedIds.push(line.id);
    }
  }
  const flagged = excess > CENT;
  const blended = payCost > 0 ? (billed / payCost - 1) * 100 : 0;
  return [
    result({
      clause,
      category: 'markup',
      label: 'Markup cap',
      status: flagged ? 'warning' : 'pass',
      invoiceValue: `${pctText(blended)} blended markup on ${usd(payCost)} pay`,
      contractValue: `Max ${maxPct}% over pay rate`,
      impactAmount: flagged ? excess : 0,
      impactNote: flagged ? `${usd(excess)} above the capped bill rates` : null,
      explanation: flagged
        ? `Bill rates average ${pctText(blended)} over worker pay, above the ${maxPct}% cap in ${section(clause)}. Markup is measured against the pay rates in the assignment letters, so confirm them with payroll before disputing.`
        : `Bill rates are within ${maxPct}% of pay (${section(clause)}).`,
      supplierStatement: flagged
        ? `Bill rates average ${pctText(blended)} over the workers' pay rates. ${section(clause)} caps the markup at ${maxPct}%. At the capped rates the difference is ${usd(excess)}.`
        : '',
      lineIds: flaggedIds,
    }),
  ];
}

function passThroughCheck(
  invoice: Invoice,
  clause: Clause,
  maxPct: number,
): CheckResult[] {
  return [
    result({
      clause,
      category: 'pass_through',
      label: 'Third-party pass-through',
      status: 'pass',
      invoiceValue: 'No pass-through charges',
      contractValue:
        maxPct === 0 ? 'At cost, no markup, pre-approved' : `Max ${maxPct}%`,
      explanation: `No third-party costs billed on ${invoice.id}, so the at-cost rule in ${section(clause)} does not apply.`,
    }),
  ];
}

function maxPctChecks(
  invoice: Invoice,
  clause: Clause,
  rule: Extract<ClauseRule, { kind: 'max_pct' }>,
): CheckResult[] {
  const subject = rule.appliesTo.toLowerCase();
  if (subject.includes('fuel')) return fuelCheck(invoice, clause, rule.maxPct);
  if (subject.includes('expenses')) {
    return expenseCapCheck(invoice, clause, rule.maxPct);
  }
  if (subject.includes('pass-through')) {
    return passThroughCheck(invoice, clause, rule.maxPct);
  }
  if (subject.includes('markup')) {
    return markupCheck(invoice, clause, rule.maxPct);
  }
  // Renewal and rate-increase caps are not evaluated on an invoice.
  return [];
}

/**
 * Cumulative billing against a budget cap. Charges that other checks
 * on the same invoice already disallow (`disallowed`) come off the invoice
 * first, so the overage is measured on what Pillar would otherwise pay and the
 * same dollars are never recovered twice.
 */
function notToExceedCheck(
  invoice: Invoice,
  clause: Clause,
  amount: number,
  disallowed: number,
): CheckResult[] {
  const prior = invoice.facts.cumulativeBilled?.[clause.id];
  if (prior === undefined) return [];
  const total = invoiceTotal(invoice);
  const cumulative = plus([prior, total]);
  const overAsBilled = minus(cumulative, amount);
  const allowable = minus(total, disallowed);
  const cumulativeAllowable = plus([prior, allowable]);
  const over = Math.min(minus(cumulativeAllowable, amount), allowable);
  const flagged = over > CENT;
  const adjusted = disallowed > CENT;
  const afterDisallowed = `After removing the ${usd(disallowed)} disallowed by the other checks, cumulative billing is ${usd(cumulativeAllowable)}`;
  return [
    result({
      clause,
      category: 'not_to_exceed',
      label: 'Budget cap',
      status: flagged ? 'violation' : 'pass',
      invoiceValue: `${usd(cumulative)} billed to date (${usd(prior)} + ${usd(total)})`,
      contractValue: `Max ${usd(amount)} fees and expenses`,
      impactAmount: flagged ? over : 0,
      impactNote: flagged
        ? adjusted
          ? `${usd(over)} above the budget cap after other disallowed charges`
          : `${usd(over)} above the budget cap`
        : null,
      explanation: flagged
        ? adjusted
          ? `Cumulative billing reaches ${usd(cumulative)} as invoiced, ${usd(overAsBilled)} over the ${usd(amount)} cap in ${section(clause)}. ${afterDisallowed}, so ${usd(over)} is still over the cap. Anything above the cap needs a change order signed before the work was done.`
          : `Cumulative billing reaches ${usd(cumulative)} against a ${usd(amount)} cap in ${section(clause)}. Anything above the cap needs a change order signed before the work was done.`
        : overAsBilled > CENT
          ? `Cumulative billing is ${usd(cumulative)} as invoiced, but ${afterDisallowed}, within the ${usd(amount)} cap (${section(clause)}).`
          : `Cumulative billing of ${usd(cumulative)} is within the ${usd(amount)} cap (${section(clause)}).`,
      supplierStatement: flagged
        ? adjusted
          ? `Cumulative billing under the statement of work reaches ${usd(cumulative)} as invoiced, above the ${usd(amount)} budget cap in ${section(clause)}. After the other charges disputed in this notice (${usd(disallowed)}), cumulative billing is ${usd(cumulativeAllowable)}, which is still ${usd(over)} over the cap. No change order has been signed for the additional amount.`
          : `Cumulative billing under the statement of work reaches ${usd(cumulative)}, which exceeds the ${usd(amount)} budget cap in ${section(clause)} by ${usd(over)}. No change order has been signed for the additional amount.`
        : '',
      headerField: flagged ? 'total' : null,
    }),
  ];
}

function rateCardCheck(
  invoice: Invoice,
  clause: Clause,
  rates: { role: string; rate: number }[],
): CheckResult[] {
  const labor = invoice.lines.filter(
    (line) => line.kind === 'labor' && line.role !== undefined,
  );
  if (labor.length === 0) return [];
  const flaggedLines: InvoiceLine[] = [];
  const notes: string[] = [];
  const contractNotes: string[] = [];
  const statements: string[] = [];
  let impact = 0;
  for (const line of labor) {
    const listed = rates.find((entry) => entry.role === line.role);
    const reference =
      listed ?? rates.find((entry) => entry.role === line.approvedRole);
    if (!reference) continue;
    const delta = minus(line.unitPrice, reference.rate);
    if (delta <= CENT) continue;
    const lineImpact = times(line.quantity, delta);
    flaggedLines.push(line);
    impact = plus([impact, lineImpact]);
    notes.push(`${line.role} at ${usd(line.unitPrice)}/h`);
    contractNotes.push(
      listed
        ? `${reference.role} max ${usd(reference.rate)}/h`
        : `No "${line.role}" role; ${reference.role} ${usd(reference.rate)}/h`,
    );
    statements.push(
      listed
        ? `${line.role} is billed at ${usd(line.unitPrice)}/h against an agreed rate of ${usd(reference.rate)}/h (${fmtNumber(line.quantity)} h x ${usd(delta)} = ${usd(lineImpact)}).`
        : `"${line.role}" is not one of the agreed roles in ${section(clause)}. The hours were approved as ${reference.role}, which is ${usd(reference.rate)}/h, so ${fmtNumber(line.quantity)} h x ${usd(delta)} = ${usd(lineImpact)} is not payable. Any other role needs a written change order before it is billed.`,
    );
  }
  const flagged = flaggedLines.length > 0;
  return [
    result({
      clause,
      category: 'rate_card',
      label: 'Agreed hourly rates',
      status: flagged ? 'violation' : 'pass',
      invoiceValue: flagged
        ? notes.join('; ')
        : 'All roles listed, at the agreed rates',
      contractValue: flagged
        ? contractNotes.join('; ')
        : rates.map((entry) => `${entry.role} ${usd(entry.rate)}`).join(', '),
      impactAmount: impact,
      impactNote: flagged ? `${usd(impact)} above the agreed rates` : null,
      explanation: flagged
        ? `${statements.join(' ')}`
        : `Every billed role is listed in ${section(clause)} at or below the agreed rate.`,
      supplierStatement: statements.join(' '),
      lineIds: flaggedLines.map((line) => line.id),
    }),
  ];
}

function slaCreditCheck(
  invoice: Invoice,
  clause: Clause,
  rule: Extract<ClauseRule, { kind: 'sla_credit' }>,
): CheckResult[] {
  const uptime = invoice.facts.uptime;
  if (!uptime) return [];
  const applicable = rule.tiers.filter((tier) => uptime.pct < tier.below);
  const creditPct = applicable.reduce(
    (best, tier) => Math.max(best, tier.creditPct),
    0,
  );
  const fee = amountOf(
    invoice.lines.filter((line) => line.kind === 'subscription'),
  );
  const due = times(fee, creditPct / 100);
  const applied = invoice.facts.creditsApplied ?? 0;
  const outstanding = minus(due, applied);
  const flagged = creditPct > 0 && outstanding > CENT;
  const uptimeText = `${uptime.pct.toFixed(2)}%`;
  return [
    result({
      clause,
      category: 'sla_credit',
      label: 'Uptime credit',
      status: flagged ? 'warning' : 'pass',
      invoiceValue: flagged
        ? `${uptimeText} availability in ${uptime.period}, no credit applied`
        : `${uptimeText} availability in ${uptime.period}`,
      contractValue: flagged
        ? `${rule.target}% target: ${creditPct}% credit = ${usd(due)}`
        : `${rule.target}% target met, no credit due`,
      impactAmount: flagged ? outstanding : 0,
      impactNote: flagged
        ? `${usd(outstanding)} credit owed to Pillar and not applied`
        : null,
      explanation: flagged
        ? `Measured availability of ${uptimeText} is below the ${rule.target}% commitment in ${section(clause)}. A ${creditPct}% credit on the ${usd(fee)} monthly fee (${usd(due)}) is owed and the invoice does not apply it.`
        : `Availability of ${uptimeText} meets the ${rule.target}% commitment in ${section(clause)}, so no credit is due.`,
      supplierStatement: flagged
        ? `Measured availability for ${uptime.period} was ${uptimeText}, below the ${rule.target}% commitment. ${section(clause)} provides a ${creditPct}% credit on the monthly fee, which is ${usd(due)}. The credit is not applied on this invoice. Please issue a credit memo for ${usd(outstanding)}.`
        : '',
      related: relatedClauseIds(invoice, clause, ['service_credits']),
      headerField: flagged ? 'total' : null,
    }),
  ];
}

function rebateCheck(
  invoice: Invoice,
  clause: Clause,
  rule: Extract<ClauseRule, { kind: 'rebate' }>,
): CheckResult[] {
  const facts = invoice.facts.rebate;
  if (!facts) return [];
  const crossed = facts.ytdNetPurchases > rule.threshold;
  // A retroactive tier (Northgate §2.2) pays the rebate on every purchase in
  // the year once the threshold is crossed. A marginal tier (Corvane §3.5)
  // pays only on the purchases above the threshold.
  const retroactive = facts.basis === 'all_purchases';
  const base = retroactive
    ? facts.ytdNetPurchases
    : facts.ytdNetPurchases - rule.threshold;
  const baseText = retroactive
    ? `all ${usd(base)} of purchases, retroactive to the first dollar`
    : `${usd(base)} of purchases above the threshold`;
  const earned = crossed ? times(base, rule.pct / 100) : 0;
  const due = minus(earned, facts.creditedYtd);
  const flagged = crossed && due > CENT;
  return [
    result({
      clause,
      category: 'rebate',
      label: 'Volume rebate',
      status: flagged ? 'warning' : 'pass',
      invoiceValue: crossed
        ? `No rebate credit on ${usd(facts.ytdNetPurchases)} YTD purchases`
        : `${fmtMoneyCompact(facts.ytdNetPurchases)} YTD purchases`,
      contractValue: crossed
        ? `${rule.pct}% of ${usd(base)} = ${usd(earned)}`
        : `Rebate starts above ${fmtMoneyCompact(rule.threshold)}`,
      impactAmount: flagged ? due : 0,
      impactNote: flagged
        ? `${usd(due)} rebate owed to Pillar, no credit note issued`
        : null,
      explanation: flagged
        ? `Year-to-date purchases reached ${usd(facts.ytdNetPurchases)}, past the ${usd(rule.threshold)} tier in ${section(clause)}. The ${rule.pct}% rebate applies to ${baseText}: ${usd(earned)}, and no credit note has been issued.`
        : `Year-to-date purchases of ${usd(facts.ytdNetPurchases)} are below the ${usd(rule.threshold)} rebate threshold in ${section(clause)}, so no rebate is due yet.`,
      supplierStatement: flagged
        ? `Year-to-date purchases reached ${usd(facts.ytdNetPurchases)}, which passes the ${usd(rule.threshold)} tier in ${section(clause)}. The ${rule.pct}% volume rebate applies to ${baseText}: ${usd(earned)}. No credit note has been issued. Please issue a credit memo for ${usd(due)}.`
        : '',
      headerField: flagged ? 'total' : null,
    }),
  ];
}

const PROOF_LINE_KIND: { keyword: string; kind: LineKind }[] = [
  { keyword: 'waiting time', kind: 'accessorial' },
  { keyword: 'overtime', kind: 'overtime' },
  { keyword: 'milestone', kind: 'milestone' },
];

function proofCheck(
  invoice: Invoice,
  clause: Clause,
  charge: string,
): CheckResult[] {
  const subject = charge.toLowerCase();
  const mapping = PROOF_LINE_KIND.find((entry) =>
    subject.includes(entry.keyword),
  );
  if (!mapping) return [];
  const lines = linesOfKind(invoice, mapping.kind);
  if (lines.length === 0) return [];
  const missing = lines.filter((line) => !line.evidence?.onFile);
  const amount = amountOf(missing);
  const flagged = missing.length > 0;

  if (mapping.kind === 'accessorial') {
    return [
      result({
        clause,
        category: 'proof',
        label: 'Waiting-time proof',
        status: flagged ? 'violation' : 'pass',
        invoiceValue: flagged
          ? `Waiting time ${usd(amount)} billed, no signed proof`
          : 'Waiting time supported by signed proof',
        contractValue: 'Signed proof from a Pillar facility representative',
        impactAmount: amount,
        impactNote: flagged
          ? `Hold ${usd(amount)} until signed proof arrives`
          : null,
        explanation: flagged
          ? `${section(clause)} makes waiting time payable only with proof signed by a Pillar facility representative. None is on file for this charge, so it is held rather than paid.`
          : `Waiting time is supported by signed proof (${section(clause)}).`,
        supplierStatement: flagged
          ? `Waiting time of ${usd(amount)} is billed without the signed proof required by ${section(clause)}. We are holding this charge until a signed gate record or bill of lading is provided.`
          : '',
        lineIds: missing.map((line) => line.id),
      }),
    ];
  }
  if (mapping.kind === 'overtime') {
    const hours = plus(missing.map((line) => line.quantity));
    return [
      result({
        clause,
        category: 'proof',
        label: 'Overtime approval',
        status: flagged ? 'warning' : 'pass',
        invoiceValue: flagged
          ? `${fmtNumber(hours)} h overtime (${usd(amount)}), no prior written approval`
          : 'Overtime approved in writing',
        contractValue: 'Prior written approval from the hiring manager',
        impactAmount: amount,
        impactNote: flagged ? `${usd(amount)} of overtime not approved` : null,
        explanation: flagged
          ? `${section(clause)} makes overtime billable only if the hiring manager approved it in writing before it was worked. No approval is on file for these hours.`
          : `Overtime has written approval (${section(clause)}).`,
        supplierStatement: flagged
          ? `${fmtNumber(hours)} hours of overtime (${usd(amount)}) are billed without the prior written approval required by ${section(clause)}. Please send the approval or remove the charge.`
          : '',
        lineIds: missing.map((line) => line.id),
      }),
    ];
  }
  const [first] = lines;
  return [
    result({
      clause,
      category: 'proof',
      label: 'Milestone acceptance',
      status: flagged ? 'violation' : 'pass',
      invoiceValue: flagged
        ? 'Milestone invoiced, no acceptance on file'
        : `Milestone invoiced, acceptance ${first.evidence?.ref ?? 'on file'}`,
      contractValue: 'Fees invoiced only on written acceptance',
      impactAmount: amount,
      impactNote: flagged ? `${usd(amount)} billed before acceptance` : null,
      explanation: flagged
        ? `${section(clause)} allows invoicing only after Pillar accepts the milestone in writing. No acceptance is on file.`
        : `Written acceptance ${first.evidence?.ref ?? ''} is on file for the milestone (${section(clause)}).`,
      supplierStatement: flagged
        ? `The milestone is invoiced without written acceptance, which ${section(clause)} requires before fees are invoiced.`
        : '',
      lineIds: flagged ? missing.map((line) => line.id) : [],
    }),
  ];
}

function evaluateRule({
  invoice,
  clause,
  rule,
  disallowed,
}: RuleClause & { invoice: Invoice; disallowed: number }): CheckResult[] {
  switch (rule.kind) {
    case 'payment_terms':
      return rule.discount
        ? earlyPayCheck(invoice, clause, rule.discount, rule.netDays)
        : termsCheck(invoice, clause, rule.netDays);
    case 'unit_price':
      return unitPriceCheck(invoice, clause, rule);
    case 'freight_included':
      return freightCheck(invoice, clause, rule.incoterm);
    case 'duties_included':
      return dutiesCheck(invoice, clause);
    case 'max_pct':
      return maxPctChecks(invoice, clause, rule);
    case 'not_to_exceed':
      return notToExceedCheck(invoice, clause, rule.amount, disallowed);
    case 'rate_card':
      return rateCardCheck(invoice, clause, rule.rates);
    case 'sla_credit':
      return slaCreditCheck(invoice, clause, rule);
    case 'rebate':
      return rebateCheck(invoice, clause, rule);
    case 'requires_proof':
      return proofCheck(invoice, clause, rule.charge);
    case 'notice':
      return [];
    default:
      return assertNever(rule);
  }
}

// ---------------------------------------------------------------------------
// PO, receipt and invoice match
// ---------------------------------------------------------------------------

export function threeWayMatch(invoice: Invoice): ThreeWayMatch {
  const poById = new Map(invoice.po.lines.map((line) => [line.id, line]));
  const invoicedByPoLine = new Map<string, number>();
  for (const line of invoice.lines) {
    if (line.poLine) {
      invoicedByPoLine.set(
        line.poLine,
        plus([invoicedByPoLine.get(line.poLine) ?? 0, line.quantity]),
      );
    }
  }
  const rows: MatchRow[] = invoice.lines.map((line) => {
    const poLine = line.poLine ? poById.get(line.poLine) : undefined;
    const received = invoice.receipt.quantities[line.id];
    const base = {
      lineId: line.id,
      description: line.description,
      unit: line.unit,
      receivedQuantity: received ?? null,
      invoiceQuantity: line.quantity,
      invoicePrice: line.unitPrice,
      invoiceAmount: line.amount,
    };
    if (!poLine) {
      return {
        ...base,
        poLineId: null,
        poQuantity: null,
        poPrice: null,
        quantityStatus: 'no_po_line',
        priceStatus: 'not_on_po',
        variance: line.amount,
      };
    }
    const poInvoiced = invoicedByPoLine.get(poLine.id) ?? line.quantity;
    const quantityOk =
      line.quantity <= (received ?? 0) + CENT &&
      poInvoiced <= poLine.quantity + CENT;
    const priceDelta = minus(line.unitPrice, poLine.unitPrice);
    const priceOk = Math.abs(priceDelta) <= CENT;
    return {
      ...base,
      poLineId: poLine.id,
      poQuantity: poLine.quantity,
      poPrice: poLine.unitPrice,
      quantityStatus: quantityOk ? 'match' : 'over',
      priceStatus: priceOk ? 'match' : 'variance',
      variance: priceOk ? 0 : times(line.quantity, priceDelta),
    };
  });
  return {
    poId: invoice.po.id,
    receiptId: invoice.receipt.id,
    receiptKind: invoice.receipt.kind,
    rows,
    quantitiesMatch: rows.every((row) => row.quantityStatus !== 'over'),
    varianceCount: rows.filter((row) => row.priceStatus !== 'match').length,
  };
}

// ---------------------------------------------------------------------------
// Standard checks, ordering, summary
// ---------------------------------------------------------------------------

function standardResult(
  invoice: Invoice,
  slug: string,
  label: string,
  status: CheckStatus,
  invoiceValue: string,
  contractValue: string,
  explanation: string,
): CheckResult {
  return {
    id: `standard:${slug}:${invoice.id}`,
    label,
    category: 'standard',
    status,
    invoiceValue,
    contractValue,
    clauseId: null,
    relatedClauseIds: [],
    impactAmount: 0,
    impactNote: null,
    explanation,
    supplierStatement: '',
    lineIds: [],
    lineCell: 'row',
    headerField: null,
  };
}

function standardChecks(invoice: Invoice, match: ThreeWayMatch): CheckResult[] {
  const supplier = getSupplier(invoice.supplierId);
  const quantityStatus: CheckStatus = match.quantitiesMatch
    ? 'pass'
    : 'violation';
  const bankStatus: CheckStatus = supplier.bank.verified ? 'pass' : 'warning';
  return [
    standardResult(
      invoice,
      'po',
      'Purchase order reference',
      'pass',
      invoice.po.id,
      'Open PO issued to this supplier',
      `${invoice.po.id} is open and issued to ${supplier.shortName}.`,
    ),
    standardResult(
      invoice,
      'quantity',
      'Quantities match receipt',
      quantityStatus,
      `${invoice.lines.length} ${invoice.lines.length === 1 ? 'line' : 'lines'} invoiced`,
      `${match.receiptKind} ${match.receiptId}`,
      match.quantitiesMatch
        ? `Invoiced quantities do not exceed the PO or the ${match.receiptKind.toLowerCase()} ${match.receiptId}.`
        : `Invoiced quantities exceed the received quantities on ${match.receiptId}.`,
    ),
    standardResult(
      invoice,
      'duplicate',
      'No duplicate invoice',
      'pass',
      invoice.id,
      'No prior invoice with the same number, supplier and amount',
      `Compared with 21,906 invoices checked this year: no match.`,
    ),
    standardResult(
      invoice,
      'bank',
      'Payment account matches supplier record',
      bankStatus,
      `Remit to ${supplier.bank.accountMasked}`,
      `${supplier.bank.bankName}, ${supplier.bank.verified ? 'verified' : 'not verified'}`,
      supplier.bank.verified
        ? 'Bank account on the invoice matches the verified account in the supplier record.'
        : 'Bank account is not verified in the supplier record.',
    ),
    standardResult(
      invoice,
      'currency',
      'Currency and tax',
      'pass',
      'USD, no tax charged',
      'USD per agreement',
      'Currency matches the agreement and no sales tax is charged on the document.',
    ),
  ];
}

const CATEGORY_ORDER: CheckCategory[] = [
  'terms',
  'price',
  'freight',
  'duties',
  'rate_card',
  'not_to_exceed',
  'expense_cap',
  'surcharge',
  'markup',
  'proof',
  'sla_credit',
  'rebate',
  'pass_through',
  'early_pay',
  'standard',
];

function categoryRank(category: CheckCategory): number {
  return CATEGORY_ORDER.indexOf(category);
}

/**
 * Dollars the invoice's violations remove from what Pillar pays. Warnings are
 * not counted: uptime credits and rebates are owed separately, and markup and
 * overtime findings do not block payment.
 */
function disallowedAmount(results: CheckResult[]): number {
  return plus(
    results
      .filter((entry) => entry.status === 'violation')
      .map((entry) => entry.impactAmount),
  );
}

/**
 * Every check for one invoice: standard checks first, then clause checks.
 * Budget cap checks run last because they measure cumulative billing after
 * the amounts the other checks disallow.
 */
export function checkInvoice(invoice: Invoice): CheckResult[] {
  const match = threeWayMatch(invoice);
  const entries = enforcedRuleClauses(invoice);
  const independent = entries
    .filter((entry) => entry.rule.kind !== 'not_to_exceed')
    .flatMap((entry) => evaluateRule({ ...entry, invoice, disallowed: 0 }));
  const disallowed = disallowedAmount(independent);
  const capped = entries
    .filter((entry) => entry.rule.kind === 'not_to_exceed')
    .flatMap((entry) => evaluateRule({ ...entry, invoice, disallowed }));
  const clauseResults = [...independent, ...capped].sort(
    (a, b) => categoryRank(a.category) - categoryRank(b.category),
  );
  return [...standardChecks(invoice, match), ...clauseResults];
}

export function summarizeChecks(results: CheckResult[]): CheckSummary {
  const flagged = results.filter((entry) => entry.status !== 'pass');
  const violations = flagged.filter(
    (entry) => entry.status === 'violation',
  ).length;
  const warnings = flagged.length - violations;
  return {
    total: results.length,
    passed: results.length - flagged.length,
    violations,
    warnings,
    recoverable: plus(flagged.map((entry) => entry.impactAmount)),
    status:
      violations > 0 ? 'violation' : warnings > 0 ? 'warning' : 'compliant',
  };
}

export function analyzeInvoice(invoice: Invoice): InvoiceAnalysis {
  const results = checkInvoice(invoice);
  const invoiceDue = invoiceDueDate(invoice);
  const contractDue = contractDueDate(invoice);
  return {
    invoice,
    total: invoiceTotal(invoice),
    results,
    flagged: results.filter((entry) => entry.status !== 'pass'),
    passed: results.filter((entry) => entry.status === 'pass'),
    summary: summarizeChecks(results),
    match: threeWayMatch(invoice),
    invoiceDue,
    contractDue,
    termsGapDays: Math.round(
      (contractDue.getTime() - invoiceDue.getTime()) / 86_400_000,
    ),
  };
}

let analysisCache: Record<InvoiceId, InvoiceAnalysis> | null = null;

function buildAnalyses(): Record<InvoiceId, InvoiceAnalysis> {
  return {
    'CV-2026-08812': analyzeInvoice(INVOICES['CV-2026-08812']),
    'LB-26-3391': analyzeInvoice(INVOICES['LB-26-3391']),
    'HP-INV-55102': analyzeInvoice(INVOICES['HP-INV-55102']),
    'TCF-778120': analyzeInvoice(INVOICES['TCF-778120']),
    'STR-INV-20931': analyzeInvoice(INVOICES['STR-INV-20931']),
    'KS-104477': analyzeInvoice(INVOICES['KS-104477']),
    'NGF-22871': analyzeInvoice(INVOICES['NGF-22871']),
    'GAI-2026-0912': analyzeInvoice(INVOICES['GAI-2026-0912']),
    'AFP-90233': analyzeInvoice(INVOICES['AFP-90233']),
    'BLC-3318': analyzeInvoice(INVOICES['BLC-3318']),
    'AIG-90412': analyzeInvoice(INVOICES['AIG-90412']),
    'BS-22188': analyzeInvoice(INVOICES['BS-22188']),
    'BVL-7741': analyzeInvoice(INVOICES['BVL-7741']),
    'SF-3398': analyzeInvoice(INVOICES['SF-3398']),
  };
}

/** Analyses of every queue invoice, computed once per page load. */
export function getAnalyses(): Record<InvoiceId, InvoiceAnalysis> {
  if (analysisCache === null) {
    analysisCache = buildAnalyses();
  }
  return analysisCache;
}

export function getAnalysis(id: InvoiceId): InvoiceAnalysis {
  return getAnalyses()[id];
}
